/* ─────────────────────────────────────────────────────────────────────────────
   D2C file URIs are bare S3 keys (e.g. "d2c/{dealId}/order_data/x.csv") — never
   render them as plain hrefs (the SPA navigates to a broken route). This helper
   signs a key via the platform-standard POST tools/refreshS3Ref (bucket + key →
   signedLink) and opens the result. Bucket is fixed per environment (backend
   application-*.properties): staging/dev/local → proshipstaging · prod → proship-prod.
   ──────────────────────────────────────────────────────────────────────────── */
import { apiConstants, post, getDownload } from '../../common/constants'

// The file lives in the bucket of the BACKEND this app talks to — so the env must be
// derived from the API base URL, NOT the frontend hostname (an Amplify "staging" FE can
// point at the prod API and vice-versa; keying off the FE host then signs the wrong
// bucket and S3 answers with an AccessDenied/NoSuchKey page).
const bucketForEnv = () => {
  const probe = String(apiConstants.BASE_LOCAL || '') || window.location.hostname
  return /(dev|staging|localhost|127\.)/i.test(probe) ? 'proshipstaging' : 'proship-prod'
}

/** S3 key (or already-signed URL) → downloadable signed URL. Returns null on failure. */
export async function s3SignedUrl(keyOrUrl) {
  if (!keyOrUrl) return null
  if (/^https?:\/\//i.test(keyOrUrl)) return keyOrUrl // already a full URL
  const res = await post(apiConstants.REFRESH_S3_URL, [{ bucketName: bucketForEnv(), key: keyOrUrl }])
  return res?.data?.responseObj?.[0]?.signedLink || null
}

/** Open an S3-key-backed document in a new tab. onError(message) fires if it can't. */
export async function openS3Doc(keyOrUrl, onError) {
  if (!keyOrUrl) { if (onError) onError('No file is available yet.'); return }
  // Open the tab synchronously (before the await) so popup blockers allow it.
  const win = window.open('', '_blank')
  try {
    const url = await s3SignedUrl(keyOrUrl)
    if (url) {
      if (win) win.location.href = url
      else window.open(url, '_blank', 'noopener')
      return
    }
    if (win) win.close()
    if (onError) onError('Could not generate the download link for this file.')
  } catch (e) {
    if (win) win.close()
    if (onError) onError(e?.message || 'Could not open the file.')
  }
}

/** On-demand PDF preview (FE Integration Guide v3 §1) — generated fresh on every call,
 * no side effects, callable at any status (unlike Share, which needs APPROVED and
 * persists to S3). Response is a raw PDF binary, not JSON, so this bypasses the RTK
 * Query slice and goes straight through the blob-aware axios helper. */
export async function exportPreviewPdf(versionId, onError) {
  if (!versionId) { if (onError) onError('Generate the proposal first — there’s no version to preview yet.'); return }
  // Open synchronously (before the await) so popup blockers allow it.
  const win = window.open('', '_blank')
  try {
    const res = await getDownload(`${apiConstants.BASE_LOCAL}${apiConstants.D2C_PROPOSAL_VERSIONS}/${versionId}/export-preview`)
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    if (win) win.location.href = url
    else window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  } catch (e) {
    if (win) win.close()
    // responseType:'blob' means an error body (e.g. the 400 for empty computedLanes)
    // also arrives as a Blob, not parsed JSON — read it as text to get the real message.
    let msg = 'Could not generate the preview PDF.'
    if (e?.response?.data instanceof Blob) {
      try { msg = JSON.parse(await e.response.data.text())?.message || msg } catch { /* not JSON */ }
    } else {
      msg = e?.response?.data?.message || e?.message || msg
    }
    if (onError) onError(msg)
  }
}
